import { publicHttp } from '@/lib/http';

/**
 * Sanitized, unauthenticated view of a payment method offered on a shared
 * invoice. Mirrors `repo.PublicPaymentMethodView` — no `business_id`,
 * `is_active`, or timestamps. `config` is a type-specific bag (e.g. a Zelle
 * phone number, a Venmo handle); render it defensively.
 */
export interface PublicPaymentMethodView {
  id: string;
  type: 'cash' | 'check' | 'zelle' | 'venmo' | 'stripe' | 'other';
  config: Record<string, unknown>;
}

/**
 * Sanitized, unauthenticated view of an invoice, resolved from a share-link
 * token. Mirrors `controller.sharedInvoiceResponse` — no IDs beyond the
 * payment methods' own, no `cdn_url`.
 */
export interface SharedInvoiceDto {
  company_name: string;
  /** The business's logo, if one is set. */
  logo_url: string | null;
  customer_name: string;
  job_name: string | null;
  status: 'unpaid' | 'pending_confirmation' | 'paid';
  subtotal: number | null;
  tax: number | null;
  discount: number | null;
  total_amount: number;
  /** "YYYY-MM-DD" or null. */
  due_date: string | null;
  sent_at: string | null;
  paid_at: string | null;
  /** RFC 3339 UTC. */
  expires_at: string;
  /** The methods this invoice actually offers — never empty in practice. */
  payment_methods: PublicPaymentMethodView[];
}

/**
 * Body for `POST /public/invoices/{token}/claim-paid` — mirrors
 * `controller.sharedClaimPaidRequest`. `payment_method_id` must be one of the
 * non-Stripe methods offered on this invoice; `claimed_by_name` is optional
 * free text (max 200).
 */
export interface SharedClaimPaidRequest {
  payment_method_id: string;
  claimed_by_name?: string;
}

export function sharedInvoiceQueryKey(token: string) {
  return ['public-invoice', token] as const;
}

/**
 * Calls `GET /public/invoices/{token}`. No auth, no cookies — uses
 * `publicHttp` so no `x-jwt` header and no 401 redirect. `404` = unknown /
 * revoked / expired token; `429` = rate limited. Fetch once per page load;
 * never poll.
 */
export async function getSharedInvoice(token: string): Promise<SharedInvoiceDto> {
  const { data } = await publicHttp.get<SharedInvoiceDto>(
    `/public/invoices/${encodeURIComponent(token)}`,
  );
  return data;
}

/**
 * Calls `POST /public/invoices/{token}/claim-paid`. No auth. Records that the
 * recipient paid by a non-Stripe method offered on the invoice, moving it
 * `unpaid` -> `pending_confirmation`. Returns the refreshed shared view.
 * `400` = payment method not offered (or is the Stripe method), `404` =
 * missing/revoked/expired token, `409` = invoice no longer unpaid.
 */
export async function claimInvoicePaid(
  token: string,
  body: SharedClaimPaidRequest,
): Promise<SharedInvoiceDto> {
  const { data } = await publicHttp.post<SharedInvoiceDto>(
    `/public/invoices/${encodeURIComponent(token)}/claim-paid`,
    body,
  );
  return data;
}

/**
 * Calls `POST /public/invoices/{token}/stripe-checkout` (no body). No auth.
 * Mints a Stripe Checkout Session on the business's connected account and
 * returns a URL to redirect the payer to — success/failure is confirmed
 * asynchronously by Stripe's webhook, not by this call's response.
 * `400` = card payment not offered or Connect onboarding incomplete, `404` =
 * missing/revoked/expired token, `409` = invoice no longer unpaid.
 */
export async function startInvoiceStripeCheckout(
  token: string,
): Promise<{ checkout_url: string }> {
  const { data } = await publicHttp.post<{ checkout_url: string }>(
    `/public/invoices/${encodeURIComponent(token)}/stripe-checkout`,
  );
  return data;
}
